import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'src/redux/store';
import { PDFConfig, PDFSection, SectionVisibilityMap } from '../types';

/**
 * Base selector for the entire PDF config
 */
export const selectPDFConfig = (state: RootState): PDFConfig =>
  state.dashboard.pdfConfig;

/**
 * Memoized selector for timeline
 * Only re-renders when timeline changes
 */
export const selectPDFTimeline = createSelector(
  [selectPDFConfig],
  (config) => config.timeline
);

/**
 * Memoized selector for generation mode
 * Only re-renders when generation mode changes
 */
export const selectPDFGenerationMode = createSelector(
  [selectPDFConfig],
  (config) => config.generationMode
);

/**
 * Memoized selector for frequency
 * Only re-renders when frequency changes
 */
export const selectPDFFrequency = createSelector(
  [selectPDFConfig],
  (config) => config.frequency
);

/**
 * Memoized selector for included sections
 * Only re-renders when included sections array changes
 */
export const selectPDFIncludedSections = createSelector(
  [selectPDFConfig],
  (config) => config.includedSections
);

/**
 * Memoized selector for custom icon
 * Only re-renders when custom icon changes
 */
export const selectPDFCustomIcon = createSelector(
  [selectPDFConfig],
  (config) => config.customIcon
);

/**
 * Memoized selector for recipients
 * Only re-renders when recipients array changes
 */
export const selectPDFRecipients = createSelector(
  [selectPDFConfig],
  (config) => config.recipients
);

/**
 * Memoized selector for section visibility map
 * Returns a map of section keys to boolean values indicating if they should be rendered
 * This is the PRIMARY selector for conditional rendering in PDF components
 *
 * @example
 * const sectionVisibility = useSelector(selectSectionVisibilityMap);
 * if (sectionVisibility[PDFSection.OVERVIEW]) {
 *   // Render overview section
 * }
 */
export const selectSectionVisibilityMap = createSelector(
  [
    selectPDFIncludedSections,
    (state: RootState) => state.keywordResearch.searchesDetails,
    (state: RootState) => state.siteAudit.websiteMonitoringDetails,
    (state: RootState) => state.localSeo.reduxFilteredLocationsData,
    (state: RootState) => state.dashboard.localSeoReportData,
    (state: RootState) => state.dashboard.pdfReportData
  ],
  (
    includedSections,
    searchesDetails: any,
    websiteMonitoringDetails: any,
    reduxFilteredLocationsData: any[],
    localSeoReportData: any,
    pdfReportData: any
  ): SectionVisibilityMap => {
    const keywordLimit = searchesDetails?.keywordLimit || 0;
    const totalAiSlots = searchesDetails?.totalAiSlots || 0;

    const hasLocalSeoData =
      reduxFilteredLocationsData?.length > 0 || !!localSeoReportData;
    const totalKeywords =
      websiteMonitoringDetails?.keywordProcessing?.keywordTrackingChipData
        ?.totalKeywords || 0;
    const hasKeywordTrackingData =
      (keywordLimit > 0 && totalKeywords > 0) ||
      !!pdfReportData?.keywordTracking;
    const hasAiVisibilityData =
      (totalAiSlots !== 0 && !!websiteMonitoringDetails?.ai_visibility) ||
      !!pdfReportData?.aiVisibilityDetails ||
      !!pdfReportData?.aiVisibility;

    return {
      [PDFSection.AI_SUMMARY]:
        includedSections.includes(PDFSection.AI_SUMMARY),
      [PDFSection.OVERVIEW]: includedSections.includes(PDFSection.OVERVIEW),
      [PDFSection.TASKS]: includedSections.includes(PDFSection.TASKS),
      [PDFSection.SCANNED_PAGES]: includedSections.includes(
        PDFSection.SCANNED_PAGES
      ),
      [PDFSection.AI_VISIBILITY]:
        includedSections.includes(PDFSection.AI_VISIBILITY) &&
        hasAiVisibilityData,
      [PDFSection.KEYWORD_TRACKING]:
        includedSections.includes(PDFSection.KEYWORD_TRACKING) &&
        hasKeywordTrackingData,
      [PDFSection.LOCAL_SEO]:
        includedSections.includes(PDFSection.LOCAL_SEO) && hasLocalSeoData
    };
  }
);

/**
 * Selector to check if a specific section is included
 * @param section - The section to check
 * @returns A selector function that returns true if the section is included
 *
 * @example
 * const isOverviewIncluded = useSelector(selectIsSectionIncluded(PDFSection.OVERVIEW));
 */
export const selectIsSectionIncluded = (section: PDFSection) =>
  createSelector([selectPDFIncludedSections], (includedSections) =>
    includedSections.includes(section)
  );

/**
 * Selector to check if any sections are selected
 * Used to show "No sections selected" placeholder
 */
export const selectHasAnySections = createSelector(
  [selectSectionVisibilityMap],
  (visibilityMap) => Object.values(visibilityMap).some((visible) => visible)
);

/**
 * Selector for compare mode
 * Returns 'first_audit' if timeline is 'compare', otherwise 'current'
 * This is used for API calls and data fetching
 */
export const selectCompareMode = createSelector(
  [selectPDFTimeline],
  (timeline) => (timeline === 'compare' ? 'first_audit' : 'current')
);

/**
 * Selector for PDF updating status
 */
export const selectIsUpdating = (state: RootState): boolean =>
  state.dashboard.isUpdating;
